import { Suspense, lazy } from "react";
import { Navigate, useRoutes } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";

import AuthPageLayout from "src/layouts/AuthPageLayout";
import MainPageLayout from "src/layouts/MainPageLayout";
import LandingPageLayout from "src/layouts/LandingPageLayout";

import AuthGuard from "src/guards/AuthGuard";
import GuestGuard from "src/guards/GuestGuard";

import { DetailModalProvider } from "src/contexts/DetailModalContext";
import { PlayerProvider } from "src/contexts/PlayerContext";
import { AuthFlowContextProvider } from "src/contexts/AuthFlowContext";

import { PATH_AUTH, PATH_DASHBOARD } from "./paths";
import { FILTERS } from "src/constants";
import { lightTheme } from "src/theme";
import YellowLogoLoadingSpinner from "src/components/Loader/YellowLogoLoadingSpinner";
import ProfileGuard from "src/guards/ProfileGuard";

const Loadable = (Component) => (props) => {
  return (
    <Suspense
      fallback={
        <YellowLogoLoadingSpinner sx={{ width: "100%", height: "100vh" }} />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Routes() {
  const routes = useRoutes([
    {
      path: "/",
      element: (
        <LandingPageLayout>
          <LandingPage />
        </LandingPageLayout>
      ),
    },
    {
      path: "auth",
      element: (
        <AuthPageLayout>
          <GuestGuard />
        </AuthPageLayout>
      ),
      children: [
        { path: "", element: <Navigate to={PATH_AUTH.login} replace /> },
        { path: "signin", element: <Signin /> },
        {
          path: "signup",
          children: [
            { path: "", element: <Signup /> },
            {
              path: "aca/:ep",
              element: (
                <AuthFlowContextProvider>
                  <AuthFlow />
                </AuthFlowContextProvider>
              ),
            },
          ],
        },
        { path: "forgot-password", element: <ForgotPassword /> },
      ],
    },
    {
      path: "/",
      element: (
        <AuthGuard>
          <ProfileGuard>
            <PlayerProvider>
              <MainPageLayout />
            </PlayerProvider>
          </ProfileGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: "browse",
          element: <DetailModalProvider />,
          children: [
            {
              path: "",
              element: (
                <HomePage
                  title=""
                  titleType="movieseries"
                  filters={[]}
                  hasSwitcher={false}
                  initialSwitcher="list"
                />
              ),
            },
            {
              path: "my-list",
              element: (
                <MyListPage
                  title="My List"
                  titleType="mylist"
                  filters={[]}
                  hasSwitcher={false}
                  initialSwitcher="grid"
                />
              ),
            },
            {
              path: "movies",
              element: (
                <MoviesPage
                  title="Movies"
                  titleType="movies"
                  filters={FILTERS}
                  hasSwitcher={true}
                  initialSwitcher="list"
                />
              ),
            },
            {
              path: "tv-shows",
              element: (
                <TvShowsPage
                  title="TV Shows"
                  titleType="series"
                  hasFilter={true}
                  filters={FILTERS.slice(0, 3)}
                  hasSwitcher={true}
                  initialSwitcher="list"
                />
              ),
            },
          ],
        },
        {
          path: "producers",
          element: (
            <ProducersPage
              title="Producers"
              titleType="producers"
              hasSwitcher={false}
              initialSwitcher="grid"
              filters={FILTERS.slice(3)}
            />
          ),
        },
        {
          path: "producers/:companyId",
          element: (
            <DetailModalProvider>
              <ProducerDetailPage />
            </DetailModalProvider>
          ),
        },
        {
          path: "search",
          element: (
            <DetailModalProvider>
              <SearchPage />
            </DetailModalProvider>
          ),
        },
        {
          path: "account",
          element: (
            <ThemeProvider theme={lightTheme}>
              <AccountPage />
            </ThemeProvider>
          ),
        },
        {
          path: "watch/:contentId",
          element: <VideoJSWatchPage />,
        },
        {
          path: "watch2/:contentId",
          element: <VideoJSWatchPage />,
        },
      ],
    },
    {
      path: PATH_DASHBOARD.manageProfiles,
      element: (
        <AuthGuard>
          <ManageProfilesPage />
        </AuthGuard>
      ),
    },
  ]);
  return routes;
}

/* eslint-disable import/first */
const Signin = Loadable(lazy(() => import("src/pages/auth/Login")));
const Signup = Loadable(lazy(() => import("src/pages/auth/Register")));
const ForgotPassword = Loadable(
  lazy(() => import("src/pages/auth/ForgotPassword"))
);

const LandingPage = Loadable(lazy(() => import("src/pages/landing/Landing")));
const HomePage = Loadable(lazy(() => import("src/pages/Home")));
const MyListPage = Loadable(lazy(() => import("src/pages/MyList")));
const MoviesPage = Loadable(lazy(() => import("src/pages/Movies")));
const ProducersPage = Loadable(
  lazy(() => import("src/pages/producers/Producers"))
);
const ProducerDetailPage = Loadable(
  lazy(() => import("src/pages/producers/ProducerDetail"))
);
const TvShowsPage = Loadable(lazy(() => import("src/pages/TvShows")));
const SearchPage = Loadable(lazy(() => import("src/pages/Search")));
const AccountPage = Loadable(lazy(() => import("src/pages/Account")));
const VideoJSWatchPage = Loadable(
  lazy(() => import("src/pages/VideoJSWatchPage"))
);
const AuthFlow = Loadable(lazy(() => import("src/pages/auth/flow")));
const ManageProfilesPage = Loadable(
  lazy(() => import("src/pages/ManageProfiles"))
);
